import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { usersDelete } from '../apis';

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: () => void;
}

export const DeleteUserDialog: FC<DeleteUserDialogProps> = ({ open, onOpenChange, onSuccess }) => {
  const t = useTranslations();

  const { updateUserInfo, resetTaskFormStates, resetToolFormStates, updateCreditsInfo, updateSubscription } = useStore(
    (state) => ({
      updateUserInfo: state.updateUserInfo,
      resetTaskFormStates: state.resetTaskFormStates,
      resetToolFormStates: state.resetToolFormStates,
      updateCreditsInfo: state.updateCreditsInfo,
      updateSubscription: state.updateSubscriptionInfo,
    }),
  );

  const { loading, run: deleteUser } = useFetch(() => usersDelete({}), {
    manual: true,
    onSuccess: ({ error }) => {
      if (error) {
        toast({
          type: 'warning',
          title: t('popup.toast.deleteUserError'),
          message: error.message,
        });
        return;
      }
      updateUserInfo(null);
      updateCreditsInfo(null);
      updateSubscription(null);
      resetTaskFormStates();
      resetToolFormStates();
      onSuccess?.();
    },
    onFinally: () => {
      onOpenChange?.(false);
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent closable={false}>
        <DialogHeader>
          <DialogTitle>{t('popup.dialog.deleteUser.title')}</DialogTitle>
          <DialogDescription>
            {t.rich('popup.dialog.deleteUser.description', {
              bold: (chunks) => <span className="font-bold">{chunks}</span>,
              br: () => <br />,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            size="dialog"
            onClick={() => {
              if (loading) return;
              deleteUser();
            }}
          >
            {loading && <Loading className="mr-2 h-4 w-4 animate-spin" />}
            {t('popup.dialog.deleteUser.confirm')}
          </Button>
          <Button variant="primary" size="dialog" onClick={() => onOpenChange?.(false)}>
            {t('popup.dialog.deleteUser.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
